/*
 * Draining one model stream into one reply.
 *
 * WHY THIS IS ITS OWN FILE rather than a few lines inside the TanStack adapter: two callers need it.
 * The loop needs the assembled text and the timings. The browser needs the same thing AND each delta
 * as it lands, because "show everything about the AI's performance live in the chat" means tokens
 * appearing as they arrive, next to a TTFT that was measured rather than estimated. Both want
 * identical accounting, and a second implementation of "when did the first token arrive" is a second
 * answer to it.
 *
 * It is deliberately transport-agnostic — it consumes AG-UI events, not HTTP. That is what lets it
 * be exercised against a synthetic event sequence, which is the only way to test the cases that
 * matter here (a stream that stalls, one that errors mid-flight, one whose first token lands in the
 * same millisecond) without a provider and a network.
 */
import type { ChatUsage } from './step';

/** Loosened view of one streamed event. The AG-UI union is wide and mostly irrelevant here;
 *  narrowing by the `type` string is what the wire actually guarantees. */
export interface StreamEventLike {
  type: string;
  delta?: string;
  message?: string;
  code?: string;
  error?: { message?: string; code?: string };
  /** TOOL_CALL_* carry the call being assembled. AG-UI streams a tool call as a START naming it,
   *  then ARGS deltas, then an END — the same shape a provider uses on the wire. */
  toolCallId?: string;
  toolCallName?: string;
}

/**
 * Every spelling of "the model is thinking out loud" that carries a delta.
 *
 * THREE, because AG-UI is mid-rename and all three are live on the wire today. `REASONING_MESSAGE_*`
 * is current, `REASONING_MESSAGE_CHUNK` is the start-less variant (the reasoning twin of
 * `TEXT_MESSAGE_CHUNK`), and `THINKING_TEXT_MESSAGE_CONTENT` is marked deprecated in the enum but is
 * not removed until AG-UI 1.0.0 — so which one arrives is the adapter's choice, not ours.
 *
 * Matching only one is the failure this set exists to prevent, and it is a QUIET one: a provider on
 * the other spelling shows no thinking at all, AND its TTFT silently becomes time-to-first-*answer*,
 * because the tokens that actually started generation were not recognised as generation. One set,
 * consumed by both the timer and the text sink, so the two cannot disagree about what thinking is.
 */
const REASONING = new Set(['REASONING_MESSAGE_CONTENT', 'REASONING_MESSAGE_CHUNK', 'THINKING_TEXT_MESSAGE_CONTENT']);

/** Streamed events that carry MODEL-GENERATED bytes, as opposed to frame boundaries.
 *
 *  TTFT is timed on these and only these. `RUN_STARTED` and `TEXT_MESSAGE_START` are lifecycle
 *  markers an adapter may synthesize locally before the wire even opens — timing on one would
 *  measure our own bookkeeping and report a flattering few milliseconds on every provider.
 *
 *  Reasoning deltas count. On a thinking model the first tokens off the wire ARE reasoning tokens,
 *  and that is the moment generation started; excluding them would report a reasoning model's TTFT
 *  as its time-to-first-*answer*, which is a different and much larger quantity. Both quantities are
 *  now reported — this one as `ttftMs`, the other implied by `reasoningMs` — rather than one being
 *  quietly served under the other's name. */
const GENERATED = new Set(['TEXT_MESSAGE_CONTENT', ...REASONING]);

/** The model stopped thinking and said so. Not required — plenty of streams go straight from a
 *  reasoning delta to a text delta — which is why the end is also inferred below. */
const REASONING_DONE = new Set(['REASONING_END', 'REASONING_MESSAGE_END', 'THINKING_END', 'THINKING_TEXT_MESSAGE_END']);

export interface DrainOptions {
  /** When the request went out. Passed in rather than captured here so it brackets the whole call
   *  including connection setup — TTFT the user feels, not TTFT after we were already connected. */
  sentAt: number;
  /**
   * How long the stream may go SILENT before it is abandoned. Not how long the
   * call may take.
   *
   * This was a deadline on the whole stream, and the comment beside it argued
   * that "a stalled stream is indistinguishable from a slow one". That is the
   * assumption that made the cap necessary, and it is false: a stream producing
   * events is working, and one producing nothing is not. Telling them apart is
   * just a matter of resetting the clock when something arrives.
   *
   * The difference is the whole point. A model composing something genuinely
   * large streams for as long as it takes and is never cut off; a connection that
   * died still frees the run holding the lease. The old shape could only trade one
   * against the other by choosing a number, and every number was wrong for one of
   * them — 120s killed real answers, and raising it would have left dead calls
   * hanging for just as long.
   */
  timeoutMs: number;
  /** Called once when the deadline expires, so the caller can abort the upstream request. The drain
   *  does not own the controller because the caller built it and handed it to the SDK. */
  onExpire?: () => void;
  /** Each text delta as it lands. The loop passes nothing; the browser renders them. */
  onDelta?: (delta: string) => void;
  /** Each REASONING delta as it lands, so a UI can show thinking while it happens rather than only
   *  announcing afterwards that it occurred. Separate hook from `onDelta` rather than a flag on it,
   *  because the two are different things on screen: one is the answer, the other is the work. */
  onReasoning?: (delta: string) => void;
  /**
   * A tool call the moment the model NAMES it, before its arguments have finished arriving and long
   * before the call this is draining returns.
   *
   * Without this, a step that asks for five tools is silent for the whole model call and then puts
   * five rows on screen at once — reported as "a big dump of tool calls in batches" after ~25s. The
   * model decided on the first one seconds into the call; there is no reason to sit on that.
   */
  onToolCall?: (call: { id: string; name: string }) => void;
  /**
   * Each fragment of a tool call's ARGUMENTS as it arrives, keyed by the call it belongs to.
   *
   * The arguments are the interesting part for exactly one kind of tool: the ones whose argument IS
   * the deliverable — a statechart, a dashboard, a document. Watching an SCXML file being written is
   * the work being done; receiving it whole when the call returns is a report that it happened.
   */
  onToolArgs?: (callId: string, delta: string) => void;
  /** Injectable for tests. Real callers get the wall clock. */
  now?: () => number;
}

export interface DrainResult {
  text: string;
  /** Absent when nothing generated arrived — never 0, which would claim an instant first token on a
   *  stream that produced none. */
  ttftMs?: number;
  /**
   * What the model thought before answering, when it streamed any.
   *
   * ABSENT, not empty, on a model that does not think or a provider that does not forward it — so
   * "this model showed no reasoning" and "this model reasoned about nothing" stay distinguishable.
   * The UI renders a thinking disclosure only when this is present, and an empty string would give
   * every non-reasoning model an empty one to open.
   */
  reasoning?: string;
  /** How long the thinking phase lasted — what "Thought for 4s" is read off. Measured start-of-first
   *  reasoning delta to end-of-thinking, so it is a real duration rather than the count of tokens. */
  reasoningMs?: number;
  /**
   * What the model asked to RUN this turn, assembled from the stream.
   *
   * Absent — not empty — when the model called nothing, so "this turn used no tools" and "this
   * deployment does not collect them" stay distinguishable. The loop treats a turn with calls as
   * progress rather than a conclusion, and that decision cannot be made from a `[]` that might mean
   * either.
   */
  toolCalls?: { id: string; name: string; argumentsJson: string }[];
}

/** The stream stopped producing. Named for the silence rather than for a duration,
 *  because the call may legitimately have been running for many minutes — what
 *  went wrong is that nothing arrived, not that it took a while. */
export class StreamTimeoutError extends Error {
  constructor(ms: number) {
    super(`model stream went silent for ${ms}ms`);
    this.name = 'StreamTimeoutError';
  }
}

/** The provider said the run failed. Distinct from a transport error because it means the request
 *  reached a model and was rejected — usually a content or quota decision, not a retry candidate. */
export class ProviderRunError extends Error {
  constructor(message: string) {
    super(`provider run error: ${message}`);
    this.name = 'ProviderRunError';
  }
}

/**
 * Consume a stream to completion, or fail trying.
 *
 * THE DEADLINE IS ENFORCED HERE, NOT ONLY BY THE ABORT CONTROLLER, and that redundancy is the point.
 * Handing an `AbortController` to the SDK relies on the adapter threading it to its fetch; an adapter
 * that quietly ignores it leaves the timeout decorative, and the failure mode is a run that hangs
 * forever holding a lease, a credit reservation, and a slot in its parent's blocking set. Racing each
 * `next()` against the deadline makes the timeout true regardless of adapter cooperation. The abort
 * still fires, because it is what actually stops the provider generating tokens we will be billed
 * for and will never read — the race bounds our wait, the abort bounds the meter.
 *
 * On timeout or provider error this THROWS and the partial text is discarded rather than returned.
 * A truncated reply that happens to parse is a fabricated deliverable, which is the failure the whole
 * propagation gate exists to prevent; a thrown step is merely a retry.
 */
export async function drainToReply(
  stream: AsyncIterable<StreamEventLike>,
  { sentAt, timeoutMs, onExpire, onDelta, onReasoning, onToolCall, onToolArgs, now = Date.now }: DrainOptions,
): Promise<DrainResult> {
  const iterator = stream[Symbol.asyncIterator]();
  let text = '';
  let ttftMs: number | undefined;
  /*
   * The thinking phase: what was thought, and the window it occupied.
   *
   * `thoughtUntil` is set when thinking demonstrably ENDED — an explicit end event, or the first
   * answer byte, whichever comes first. Inferring it from the first answer byte is what makes this
   * correct on the providers that send no end event at all, which is most of them. It is also why
   * the duration is not simply last-delta minus first-delta: that would stop the clock at the last
   * thinking token and hide the pause between thinking and answering, reporting a model as faster to
   * think than it was.
   */
  let reasoning = '';
  let thoughtFrom: number | undefined;
  let thoughtUntil: number | undefined;
  /** The instant the event being handled arrived. One clock read per event, shared by every
   *  measurement taken from it — reading `now()` twice for the same event can straddle a
   *  millisecond boundary and make a thought that began and ended on one token look 1ms long. */
  let at = 0;
  const stopThinking = (): void => {
    if (thoughtFrom !== undefined) thoughtUntil ??= at;
  };
  /*
   * Tool calls, assembled in ARRIVAL ORDER.
   *
   * A Map keyed by call id rather than an array indexed by position: providers interleave the ARGS
   * deltas of parallel calls, so "the last one started" is not the one the next delta belongs to.
   * Insertion order is preserved by Map, which is the order the model asked for them — and running
   * them out of order would change behaviour for any sequence where one call sets up the next.
   */
  const toolCalls = new Map<string, { id: string; name: string; argumentsJson: string }>();

  let expire!: () => void;
  const expired = new Promise<'expired'>((resolve) => {
    expire = () => resolve('expired');
  });
  /*
   * A RE-ARMABLE timer, which is what turns a duration cap into a silence one.
   *
   * Cleared and set again on every event that arrives, so the window only ever
   * measures the gap since the last one. A stream that keeps producing keeps
   * pushing the deadline out and is never cut off, however long the whole call
   * runs; one that stops is abandoned a window later, which is the case this
   * exists for.
   */
  let timer = setTimeout(fire, timeoutMs);
  function fire(): void {
    onExpire?.(); // stop the provider generating; the race below stops us waiting
    expire();
  }
  const bump = (): void => {
    clearTimeout(timer);
    timer = setTimeout(fire, timeoutMs);
  };

  try {
    for (;;) {
      const pending = iterator.next();
      // The losing promise stays in flight. Once the deadline wins we abort the request, which
      // rejects it — with nobody awaiting, that surfaces as an unhandled rejection and, depending on
      // the host, can take the process down. Swallow it explicitly: we have already decided this
      // call failed, and its late rejection carries no new information.
      pending.catch(() => {});
      const step = await Promise.race([pending, expired]);
      if (step === 'expired') throw new StreamTimeoutError(timeoutMs);

      const { value, done } = step;
      if (done) break;
      // Reset BEFORE the event is interpreted, and for every event rather than
      // only the ones carrying text: a provider sending keep-alives or tool-call
      // fragments is a live connection, and treating those as silence would
      // abandon a call that is plainly working.
      bump();
      if (!value) continue;

      at = now();
      if (GENERATED.has(value.type)) {
        // `??=`, never `||=`. A first token arriving inside the same millisecond is a real
        // measurement of 0, and `||=` would discard it and re-time against a later chunk —
        // reporting the fastest case as if it were slower.
        ttftMs ??= at - sentAt;
      }
      if (REASONING.has(value.type)) {
        // `_CHUNK` makes delta optional, so a frame carrying only a messageId is a real event that
        // adds no text. Recording the start regardless is still right — thinking HAS begun.
        thoughtFrom ??= at;
        const delta = value.delta ?? '';
        reasoning += delta;
        if (delta) onReasoning?.(delta);
      }
      if (REASONING_DONE.has(value.type)) stopThinking();
      // A tool call is generated output too — the model produced it, and on a turn that calls a
      // tool without writing prose these are the ONLY generated bytes. Timing TTFT on text alone
      // would report no first token at all for that turn.
      if (value.type === 'TOOL_CALL_START' && value.toolCallId) {
        ttftMs ??= now() - sentAt;
        // Deciding to call a tool is a conclusion — the thinking that led to it is over.
        stopThinking();
        toolCalls.set(value.toolCallId, { id: value.toolCallId, name: value.toolCallName ?? '', argumentsJson: '' });
        onToolCall?.({ id: value.toolCallId, name: value.toolCallName ?? '' });
      }
      if (value.type === 'TOOL_CALL_ARGS' && value.toolCallId) {
        const call = toolCalls.get(value.toolCallId);
        // A stream that sends ARGS for a call it never started is malformed, but the arguments are
        // still real — keep them rather than dropping the call on the floor.
        if (call) call.argumentsJson += value.delta ?? '';
        if (value.delta) onToolArgs?.(value.toolCallId, value.delta);

        else toolCalls.set(value.toolCallId, { id: value.toolCallId, name: '', argumentsJson: value.delta ?? '' });
      }
      if (value.type === 'TEXT_MESSAGE_CONTENT') {
        // The first byte of the ANSWER ends the thinking phase, on the providers that never say so.
        stopThinking();
        const delta = value.delta ?? '';
        text += delta;
        if (delta) onDelta?.(delta);
      }
      // Some adapters surface a provider failure as an event instead of rejecting the iterator.
      // Left unhandled it would end the loop cleanly with an empty reply, which reads downstream as
      // a model that answered nothing rather than a call that failed.
      if (value.type === 'RUN_ERROR') {
        // The CODE too, where the adapter supplies one. It was discarded, so every provider failure
        // reached the user as the same undifferentiated sentence — and a rate limit, a bad request
        // and an outage want three different responses.
        const code = value.code ?? value.error?.code ?? '';
        const msg = value.message ?? value.error?.message ?? 'unknown';
        throw new ProviderRunError(code ? `${msg} (${code})` : msg);
      }
    }
  } finally {
    clearTimeout(timer);
    // Let the producer release its socket. A `for await` does this on early exit; driving the
    // iterator by hand means doing it explicitly, and skipping it leaks a connection per failed call.
    if (iterator.return) await iterator.return(undefined).catch(() => {});
  }

  // A stream that ended mid-thought — no end event, no answer — still thought for a measurable
  // time. Closing the window here rather than leaving it open keeps the duration reportable, and
  // takes a fresh instant because the last event's is now in the past.
  at = now();
  stopThinking();

  // Absent, not empty — see DrainResult.toolCalls and DrainResult.reasoning.
  return {
    text,
    ttftMs,
    ...(reasoning ? { reasoning, reasoningMs: (thoughtUntil ?? thoughtFrom!) - thoughtFrom! } : {}),
    ...(toolCalls.size ? { toolCalls: [...toolCalls.values()] } : {}),
  };
}

export type { ChatUsage };
